import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { getClientConfig } from "../api";
import { useT } from "../i18n";

/**
 * The install bar (requirement 0138, ADR-0209 §5): on a phone, a book or
 * chapter page opened from a shared link offers the app — one quiet line with
 * that platform's link, which the server holds per app platform and can change
 * without a release. No link for this phone, a desktop, or a dismissal: no
 * bar. It sits in the page's flow above the content, never over the reading
 * text, and the web reader stays whole without it.
 *
 * Never on an iPhone (ADR-0213 §6): the iOS link that would show the bar is
 * the one that makes the server add Safari's own banner, so the page would
 * show both.
 */

const DISMISSED = "sageleaf.installBar.dismissed";

/** Which app a user agent's device runs, or null for anything but a phone. */
export function appPlatform(userAgent: string): "ios" | "android" | null {
  if (/iPhone|iPod/.test(userAgent)) return "ios";
  if (/Android/.test(userAgent) && /Mobile/.test(userAgent)) return "android";
  return null;
}

/** Which phone gets the bar: Android only, since Safari's banner is iOS's. */
export function barPlatform(userAgent: string): "android" | null {
  return appPlatform(userAgent) === "android" ? "android" : null;
}

function wasDismissed(): boolean {
  try {
    return localStorage.getItem(DISMISSED) === "1";
  } catch {
    return false;
  }
}

export default function InstallBar({ className = "" }: { className?: string }) {
  const t = useT();
  const platform = barPlatform(navigator.userAgent);
  const [dismissed, setDismissed] = useState(wasDismissed);
  const config = useQuery({
    queryKey: ["client-config"],
    queryFn: getClientConfig,
    enabled: !!platform && !dismissed,
    staleTime: Infinity,
    retry: false,
  });
  const link = platform ? config.data?.install_urls?.[platform] : undefined;
  if (!link || dismissed) return null;

  const dismiss = () => {
    setDismissed(true);
    try {
      localStorage.setItem(DISMISSED, "1");
    } catch {
      // Dismissed for this visit.
    }
  };

  return (
    <div
      data-testid="install-bar"
      className={`flex items-center gap-3 rounded-card border border-line bg-surface px-3 py-2 text-sm ${className}`}
    >
      <p className="min-w-0 flex-1 text-ink-soft">{t.install.prompt}</p>
      <a
        href={link}
        data-testid="install-link"
        rel="noopener"
        className="shrink-0 rounded-full bg-sage px-3 py-1 text-xs font-semibold text-on-sage"
      >
        {t.install.get}
      </a>
      <button
        type="button"
        data-testid="install-dismiss"
        aria-label={t.install.dismiss}
        onClick={dismiss}
        className="grid size-7 shrink-0 place-items-center rounded-full text-ink-soft transition-colors hover:bg-sunk hover:text-ink"
      >
        <span aria-hidden>×</span>
      </button>
    </div>
  );
}
